import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect, useRef } from "react";
import { Bold, Italic, List, ListOrdered, Heading2, Heading3, Link as LinkIcon, Image as ImageIcon, Quote, Undo, Redo } from "lucide-react";
import api from "../../lib/api";

export default function RichEditor({ value, onChange, placeholder = "Écrivez votre contenu…", testid = "rich-editor" }) {
  const fileRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      Image,
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt("URL du lien :");
    if (url) editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      editor.chain().focus().setImage({ src: data.url }).run();
    } catch (err) {
      window.alert("Échec de l'envoi");
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const Btn = ({ onClick, active, children, label }) => (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={`p-2 rounded-lg transition ${active ? "bg-[#2D2A26] text-white" : "hover:bg-[#F6F3EC] text-[#2D2A26]"}`}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-[#2D2A26]/15 rounded-xl overflow-hidden" data-testid={testid}>
      <div className="bg-[#FDFBF7] border-b border-[#2D2A26]/10 p-2 flex flex-wrap gap-1">
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} label="Titre H2"><Heading2 size={16} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} label="Titre H3"><Heading3 size={16} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} label="Gras"><Bold size={16} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} label="Italique"><Italic size={16} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} label="Liste"><List size={16} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} label="Liste numérotée"><ListOrdered size={16} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} label="Citation"><Quote size={16} /></Btn>
        <Btn onClick={addLink} active={editor.isActive("link")} label="Lien"><LinkIcon size={16} /></Btn>
        <Btn onClick={() => fileRef.current?.click()} label="Image"><ImageIcon size={16} /></Btn>
        <div className="ml-auto flex gap-1">
          <Btn onClick={() => editor.chain().focus().undo().run()} label="Annuler"><Undo size={16} /></Btn>
          <Btn onClick={() => editor.chain().focus().redo().run()} label="Refaire"><Redo size={16} /></Btn>
        </div>
        <input ref={fileRef} type="file" accept="image/*" onChange={uploadImage} className="hidden" />
      </div>
      <EditorContent editor={editor} className="prose-editor px-5 py-4 min-h-[280px] max-w-none focus:outline-none" />
    </div>
  );
}
